import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Leaf, Heart, Sparkles, Phone, Mail, MapPin, Clock, Star, Instagram } from 'lucide-react';
import { mockGallery } from '../mock/mock';
import { ServicesSection } from '../components/sections/ServicesSection';
import { TarifsSection } from '../components/sections/TarifsSection';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { FaqSection } from '../components/sections/FaqSection';
import '../styles/home.css';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Merci! Votre demande a été enregistrée. Nous vous contacterons bientôt.');
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="home-container">
      <Helmet>
        <title>Laura Body | Naturopathe, Réflexologue et Massage à Angoulême (16000)</title>
        <meta name="description" content="Laura Body, naturopathe et réflexologue à Angoulême (16000). Réflexologie plantaire, massage Shirotchampi, Massage Rose Équilibre et naturopathie. Accompagnement naturel personnalisé." />
        <link rel="canonical" href="https://www.naturopathe-angouleme.fr/" />
      </Helmet>
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-container">
            <div className="logo-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_nature-health-2/artifacts/zktrrb9e_Logo%20chouquinette.png" 
                alt="Laura Body Logo" 
                className="logo-img"
              />
            </div>
            <div className="logo-text">
              <span className="logo-body">Laura Body</span>
              <span className="logo-nature">Reflexologue - Naturopathe</span>
            </div>
          </div>
          <nav className="nav-menu">
            <a href="#accueil" className="nav-link">Accueil</a>
            <a href="#apropos" className="nav-link">À propos</a>
            <a href="/services" className="nav-link">Services</a>
            <a href="/tarifs" className="nav-link">Tarifs</a>
            <a href="/reflexologie-angouleme" className="nav-link">Réflexologie</a>
            <a href="/naturopathe-angouleme" className="nav-link">Naturopathie</a>
            <a href="/blog" className="nav-link">Blog</a>
            <a href="#contact" className="nav-link">Contact</a>
          </nav>
          <div className="header-actions">
            <a href="tel:0650155923" className="header-phone" data-testid="header-phone">
              <Phone size={18} />
              <span className="header-phone-text">06 50 15 59 23</span>
            </a>
            <a 
              href="https://perfactive.fr/laura-body/new?people=26749" target="_blank" 
            >
              <Button className="btn-reservation">Réserver</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Bouton flottant mobile */}
      <div className="mobile-floating-bar" data-testid="mobile-floating-bar">
        <a href="tel:0650155923" className="floating-phone-btn">
          <Phone size={20} />
          <span>Appeler</span>
        </a>
        <a 
          href="https://perfactive.fr/laura-body/new?people=26749" target="_blank"
          className="floating-reserve-btn"
        >
          <span>Réserver</span>
        </a>
      </div>

      {/* Hero Section */}
      <section id="accueil" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles className="badge-icon" />
            <span>Bien-être Naturel</span>
          </div>
          <h1 className="hero-title">
            Laura Body – Naturopathe et Réflexologue à Angoulême <br />
            <span className="hero-title-accent">Prenez soin de vous, naturellement.</span>
          </h1>
          <p className="hero-description">
            Laura Body vous accompagne en réflexologie plantaire, naturopathie et massage bien-être à Angoulême
          </p>
          <div className="hero-buttons">
            <a href="#services">
              <Button size="lg" className="btn-primary-hero">
                <Heart className="btn-icon" />
                Découvrir nos soins
              </Button>
            </a>
            <a 
              href="https://perfactive.fr/laura-body/new?people=26749" target="_blank" 
            >
              <Button size="lg" variant="outline" className="btn-secondary-hero">
                <Phone className="btn-icon" />
                Réserver une séance
              </Button>
            </a>
          </div>
          <div className="google-rating-hero" data-testid="google-rating-hero">
            <div className="google-rating-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={`hero-star-${i}`} className="google-star" fill="currentColor" />
              ))}
            </div>
            <span className="google-rating-text">5/5 sur Google</span>
            <span className="google-rating-count">- 12 avis</span>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-indicator"></div>
        </div>
      </section>

      {/* À Propos Section */}
      <section id="apropos" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-image">
              <div className="about-image-wrapper">
              </div>
            </div>
            <div className="about-content">
              <span className="section-label">Ma promesse</span>
              <h2 className="section-title">Laura Body, Votre Naturopathe et Réflexologue à Angoulême</h2>
              <p className="about-text">
                Naturopathe et réflexologue installée à Angoulême, je suis Laura Body et j'ai la conviction que la nature détient les clés de l'harmonie entre le corps et l'esprit. Mon cabinet, situé au 116 rue de Saintes, vous accueille dans un cadre chaleureux et apaisant.
              </p>
              <p className="about-text">
                Que ce soit pour une consultation en naturopathie, une séance de réflexologie plantaire ou un massage bien-être, je vous propose un accompagnement personnalisé selon vos besoins spécifiques, dans un environnement qui favorise la détente profonde et la régénération naturelle.
              </p>
              <div className="about-values">
                <div className="value-item">
                  <div className="value-icon">
                    <Heart />
                  </div>
                  <div>
                    <h4>Écoute & Bienveillance</h4>
                    <p>Une attention personnalisée pour chaque consultant</p>
                  </div>
                </div>
                <div className="value-item">
                  <div className="value-icon">
                    <Leaf />
                  </div>
                  <div>
                    <h4>Une approche globale</h4>
                    <p>Un mieux être pour un mieux vivre</p>
                  </div>
                </div>
                <div className="value-item">
                  <div className="value-icon">
                    <Sparkles />
                  </div>
                  <div>
                    <h4>Excellence</h4>
                    <p>Une approche naturelle et efficace pour votre bien être</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <ServicesSection />

      <TarifsSection />

      <TestimonialsSection />

      {/* Galerie Section */}
      <section id="galerie" className="gallery-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Notre Espace</span>
            <h2 className="section-title">Cabinet de Bien-être - Naturopathe à Angoulême</h2>
          </div>
          <div className="gallery-carousel-wrapper">
            <Carousel className="gallery-carousel">
              <CarouselContent>
                {mockGallery.map((image) => (
                  <CarouselItem key={image.url}>
                    <div className="gallery-carousel-item">
                      <img 
                        src={image.url} 
                        alt={image.alt}
                        className="gallery-carousel-image"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="carousel-btn-prev" />
              <CarouselNext className="carousel-btn-next" />
            </Carousel>
          </div>
        </div>
      </section>

      <FaqSection />

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            <div className="contact-info">
              <span className="section-label">Contact</span>
              <h2 className="section-title">Réservez Votre Séance avec Laura Body</h2>
              <p className="contact-description">
                Prenez un moment pour vous. Contactez Laura Body, naturopathe et réflexologue à Angoulême, pour réserver votre séance de massage, réflexologie ou naturopathie.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone />
                  </div>
                  <div>
                    <div className="contact-label">Téléphone</div>
                    <a href="tel:0650155923" className="contact-value">06 50 15 59 23</a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail />
                  </div>
                  <div>
                    <div className="contact-label">Email</div>
                    <a href="mailto:contact@naturopathe-angouleme.fr" className="contact-value">contact@naturopathe-angouleme.fr</a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin />
                  </div>
                  <div>
                    <div className="contact-label">Adresse</div>
                    <a href="https://www.google.com/maps/search/?api=1&query=116+rue+de+Saintes+16000+Angoulême" target="_blank" rel="noopener" className="contact-value">116 rue de Saintes, 16000 Angoulême</a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Clock />
                  </div>
                  <div>
                    <div className="contact-label">Horaires</div>
                    <div className="contact-value">Mardi au Samedi : 09h00 - 19h00</div>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Instagram />
                  </div>
                  <div>
                    <div className="contact-label">Instagram</div>
                    <a 
                      href="https://www.instagram.com/laurabody.naturopathe?igsh=MWt6cXlibzBrbGp0eg==" 
                      target="_blank" 
                      rel="noopener"
                      className="contact-value instagram-link"
                    >
                      @laurabody.naturopathe
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <div className="reservation-content">
                  <h3 className="reservation-title">Réservez votre séance en ligne</h3>
                  <p className="reservation-description">
                    Choisissez le créneau qui vous convient et réservez directement votre consultation 
                    de naturopathie ou séance de réflexologie plantaire.
                  </p>
                  <a 
                    href="https://perfactive.fr/laura-body/new?people=26749" target="_blank" 
                    className="reservation-link-button"
                  >
                    <Button className="btn-reservation-large" size="lg">
                      <Heart className="btn-icon" />
                      Réserver ma séance
                    </Button>
                  </a>
                  <p className="reservation-note">
                    Vous serez redirigé vers notre calendrier de réservation en ligne sécurisé
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-grid">
            <div className="footer-column">
              <div className="footer-logo">
                <Leaf className="footer-logo-icon" />
                <div>
                  <div className="footer-logo-text">Laura Body</div>
                  <div className="footer-logo-subtitle">Naturopathe - Reflexologue</div>
                </div>
              </div>
              <p className="footer-description">
                Laura Body, votre naturopathe, réflexologue et masseuse bien-être à Angoulême (16000, Charente). Consultations en naturopathie, réflexologie plantaire, massage Shirotchampi et Massage Rose Équilibre.
              </p>
              <a 
                href="https://www.instagram.com/laurabody.naturopathe?igsh=MWt6cXlibzBrbGp0eg==" 
                target="_blank" 
                rel="noopener"
                className="footer-instagram-btn"
              >
                <Instagram className="footer-instagram-icon" />
                <span>Suivez-moi sur Instagram</span>
              </a>
            </div>
            <div className="footer-column">
              <h4 className="footer-title">Liens Rapides</h4>
              <ul className="footer-links">
                <li><a href="/">Accueil</a></li>
                <li><a href="/services">Services</a></li>
                <li><a href="/tarifs">Tarifs</a></li>
                <li><a href="/blog">Blog</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h4 className="footer-title">Services</h4>
              <ul className="footer-links">
                <li><a href="/naturopathe-angouleme">Naturopathie Angoulême</a></li>
                <li><a href="/reflexologie-angouleme">Réflexologie Angoulême</a></li>
                <li><a href="#services">Naturopathie</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h4 className="footer-title">Contact</h4>
              <ul className="footer-links">
                <li><a href="tel:0650155923">06 50 15 59 23</a></li>
                <li><a href="mailto:contact@naturopathe-angouleme.fr">contact@naturopathe-angouleme.fr</a></li>
                <li><a href="https://www.google.com/maps/search/?api=1&query=116+rue+de+Saintes+16000+Angoulême" target="_blank" rel="noopener">116 rue de Saintes</a></li>
                <li><a href="https://www.google.com/maps/search/?api=1&query=116+rue+de+Saintes+16000+Angoulême" target="_blank" rel="noopener">16000 Angoulême</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Laura Body. Tous droits réservés.</p>
            <div className="footer-bottom-links">
              <a href="/mentions-legales">Mentions légales</a>
              <a href="/mentions-legales">Politique de confidentialité</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
